(() => {
  const normaliseLocale = (value) => String(value || '').trim().toLowerCase().replaceAll('_', '-');
  const optionKeyAliases = {
    estandar: 'standard',
    'paquete-estandar': 'standard',
    'paquete-elite': 'elite-pack',
    'paquete-de-campo': 'field-pack',
    'pack-elite': 'elite-pack',
    'pack-field': 'field-pack',
    'gris-sombra': 'shadow-grey',
    'verde-edge': 'edge-green'
  };
  const normaliseOptionKey = (value) => {
    const key = String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return optionKeyAliases[key] || key;
  };

  const initialiseGallery = (gallery) => {
    if (gallery.dataset.variantImageGroupsReady === 'true') return;

    const section = gallery.closest('.js-product');
    const picker = section?.querySelector('variant-picker');
    if (!section || !picker || typeof gallery.setActiveMedia !== 'function') return;

    gallery.dataset.variantImageGroupsReady = 'true';
    const locale = normaliseLocale(gallery.dataset.currentLocale);
    const language = normaliseLocale(gallery.dataset.currentLanguage || locale.split('-')[0]);
    let currentGroupKey;

    const mainMetaImages = () => [...gallery.querySelectorAll('.main-image .slider__item[data-gallery-bundle-key][data-gallery-color-key]')];
    const activeMediaId = () => gallery.querySelector('.main-image .slider__item.is-active')?.dataset.mediaId;

    const getSelectedKeys = (variant) => {
      const bundleValue = picker.querySelector('.new-variant-picker__selector--bundle input:checked')?.value;
      const colorValue = picker.querySelector('.new-variant-picker__selector--color input:checked')?.value;
      const optionKeys = (variant?.options || []).map(normaliseOptionKey);
      const availableBundleKeys = new Set(mainMetaImages().map((item) => item.dataset.galleryBundleKey));
      const availableColorKeys = new Set(mainMetaImages().map((item) => item.dataset.galleryColorKey));
      return {
        bundleKey: normaliseOptionKey(bundleValue) || optionKeys.find((key) => availableBundleKeys.has(key)),
        colorKey: normaliseOptionKey(colorValue) || optionKeys.find((key) => availableColorKeys.has(key))
      };
    };

    const selectGroupImages = (bundleKey, colorKey) => {
      const groupImages = mainMetaImages().filter((item) => (
        item.dataset.galleryBundleKey === bundleKey && item.dataset.galleryColorKey === colorKey
      ));
      const exactImages = groupImages.filter((item) => normaliseLocale(item.dataset.galleryLanguage) === locale);
      if (exactImages.length) return exactImages;

      const languageImages = groupImages.filter((item) => normaliseLocale(item.dataset.galleryLanguage) === language);
      if (languageImages.length) return languageImages;

      return groupImages.filter((item) => item.dataset.galleryFallback === 'true');
    };

    const refreshCarousels = () => {
      const carousels = new Set();
      gallery.mediaLists?.forEach((list) => list.forEach((item) => {
        const carousel = item.closest('carousel-slider');
        if (carousel) carousels.add(carousel);
      }));
      carousels.forEach((carousel) => {
        if (typeof carousel.refresh === 'function') carousel.refresh();
      });
    };

    const updateProgress = (visibleIds, selectedId) => {
      const progress = gallery.querySelector('.slider-progress-fill');
      if (!progress) return;

      const ids = [...visibleIds];
      const index = Math.max(ids.indexOf(String(selectedId)), 0);
      progress.dataset.total = ids.length;
      progress.dataset.current = ids.length ? index + 1 : 0;
      progress.style.width = ids.length ? `${((index + 1) / ids.length) * 100}%` : '0%';
    };

    const applyVariant = (variant) => {
      const variantId = variant?.id ? String(variant.id) : '';
      const { bundleKey, colorKey } = getSelectedKeys(variant);
      const selectedMetaImages = selectGroupImages(bundleKey, colorKey);
      const selectedMetaIds = new Set(selectedMetaImages.map((item) => String(item.dataset.mediaId)));
      const hasMetaGroup = selectedMetaIds.size > 0;
      const productFallbackImages = [...gallery.querySelectorAll('.main-image .slider__item[data-gallery-product-fallback]')];
      const visibleImages = hasMetaGroup ? selectedMetaImages : productFallbackImages;
      const visibleImageIds = new Set(visibleImages.map((item) => String(item.dataset.mediaId)));
      const nextGroupKey = hasMetaGroup
        ? `${bundleKey}:${colorKey}:${selectedMetaImages[0]?.dataset.galleryLanguage || 'fallback'}`
        : `${bundleKey}:${colorKey}:product-fallback`;
      const groupChanged = currentGroupKey !== undefined && currentGroupKey !== nextGroupKey;
      currentGroupKey = nextGroupKey;
      gallery.dataset.variantImageGroupKey = nextGroupKey;
      gallery.dataset.variantImageGroupSource = hasMetaGroup ? 'metaobject' : 'product-media';

      gallery.mediaLists?.forEach((list) => list.forEach((item) => {
        const mediaId = String(item.dataset.mediaId || '');
        if (item.hasAttribute('data-gallery-bundle-key')) {
          item.hidden = !selectedMetaIds.has(mediaId);
        } else if (item.hasAttribute('data-gallery-product-fallback')) {
          item.hidden = hasMetaGroup;
        }
      }));

      const firstImage = visibleImages[0];
      const imageButton = gallery.querySelector('.new-product-media-switcher__button[data-media-type="image"]');
      const updateImageButton = () => {
        if (!imageButton) return;
        imageButton.disabled = !firstImage;
        imageButton.dataset.mediaId = firstImage?.dataset.mediaId || '';
        imageButton.dataset.mediaCount = visibleImages.length;
      };
      updateImageButton();

      refreshCarousels();

      const currentId = activeMediaId();
      const imageTypeSelected = !gallery.dataset.selectedMediaType || gallery.dataset.selectedMediaType === 'image';
      const nextId = groupChanged || !visibleImageIds.has(String(currentId))
        ? firstImage?.dataset.mediaId
        : currentId;
      if (imageTypeSelected && nextId && String(nextId) !== String(currentId)) {
        gallery.setActiveMedia(nextId, true);
      }

      updateProgress(visibleImageIds, nextId || currentId);
      gallery.dispatchEvent(new CustomEvent('new-product:variant-images-filtered', {
        detail: {
          visibleImageIds: [...visibleImageIds],
          source: hasMetaGroup ? 'metaobject' : 'product-media',
          variantId,
          bundleKey,
          colorKey
        }
      }));
      updateImageButton();
      queueMicrotask(updateImageButton);
    };

    section.addEventListener('on:variant:change', (event) => applyVariant(event.detail?.variant));
    section.addEventListener('variant:changed', (event) => applyVariant(event.detail?.variant || event.detail));
    picker.addEventListener('change', () => applyVariant(picker.getSelectedVariant()));
    applyVariant(picker.getSelectedVariant());
  };

  const initialiseAll = () => {
    if (!customElements.get('media-gallery') || !customElements.get('variant-picker')) {
      Promise.all([
        customElements.whenDefined('media-gallery'),
        customElements.whenDefined('variant-picker')
      ]).then(initialiseAll);
      return;
    }

    document.querySelectorAll('media-gallery[data-variant-image-groups]').forEach(initialiseGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  } else {
    initialiseAll();
  }
  document.addEventListener('shopify:section:load', initialiseAll);
})();
